import { Link } from 'react-router-dom'
import { useProject } from '../components/ProjectLayout.js'
import { CopyButton, ErrorBox, PageHeader, relative } from '../components/ui.js'
import { useApi, useAsync } from '../lib/useApi.js'

export default function ProjectDetail() {
  const { project: p, reload } = useProject()
  const api = useApi()
  const auth = useAsync(() => api.projectAuth(p.id), [api, p.id])
  const site = useAsync(() => api.site(p.id), [api, p.id])
  const functions = useAsync(() => api.listFunctions(p.id), [api, p.id])
  const models = useAsync(() => api.aiModels(p.id), [api, p.id])
  const capabilities = useAsync(() => api.describeProject(p.id), [api, p.id, p.status])
  const path = `/projects/${p.id}`
  const snippet = [
    `curl ${p.dataPlaneUrl ?? '<project-endpoint>'}/v1/sql \\`,
    '  -H "Authorization: Bearer <server-only-service-key>" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"sql":"select 1 as ok"}'`,
  ].join('\n')
  return (
    <>
      <PageHeader
        title="Project overview"
        description="Your backend at a glance. Pick up where you left off."
        actions={
          <button
            className="btn"
            onClick={() => {
              reload()
              auth.reload()
              site.reload()
              functions.reload()
              models.reload()
              capabilities.reload()
            }}
          >
            Refresh status
          </button>
        }
      />
      {p.status !== 'active' && (
        <div className="notice">
          <strong>Project is {p.status}.</strong>
          <p>
            Some services may be unavailable. <Link to={`${path}/settings`}>Review project settings</Link> or{' '}
            <Link to={`${path}/diagnostics`}>run diagnostics</Link>.
          </p>
        </div>
      )}
      <div className="overview-banner">
        <div>
          <span className="eyebrow">PROJECT DETAILS</span>
          <h2>{p.name}</h2>
          <p className="sub">
            Created {relative(p.createdAt)} · {p.region}
          </p>
        </div>
        <div className="engine-label">
          <strong>{p.databaseTier === 'd1' ? 'Cloudflare D1' : 'Neon PostgreSQL'}</strong>
          <span className="sub small">
            {p.databaseTier === 'd1' ? 'SQLite database' : 'PostgreSQL database'}
          </span>
        </div>
      </div>
      <div className="section-head">
        <h2>Build with your backend</h2>
        <span className="sub small">Manage your project services</span>
      </div>
      <div className="service-grid">
        <Link className="service-card" to={`${path}/database`}>
          <span className="service-icon" aria-hidden="true">
            ▤
          </span>
          <h3>
            Database <span aria-hidden="true">↗</span>
          </h3>
          <p>Browse tables, manage access policies and run SQL.</p>
          <span className="service-meta">
            {p.databaseTier.toUpperCase()} · {p.status}
          </span>
        </Link>
        <Link className="service-card" to={`${path}/deployments`}>
          <span className="service-icon" aria-hidden="true">
            ↗
          </span>
          <h3>
            Deployments <span aria-hidden="true">↗</span>
          </h3>
          <p>Your site, edge functions and environment variables.</p>
          <span className="service-meta">
            {functions.loading
              ? 'Loading functions…'
              : functions.error
                ? 'Function status unavailable'
                : `${functions.data?.functions.length ?? 0} functions`}{' '}
            ·{' '}
            {site.loading
              ? 'Loading site…'
              : site.error
                ? 'Site status unavailable'
                : site.data?.deployed
                  ? 'Site deployed'
                  : 'No site deployed'}
          </span>
        </Link>
        <Link className="service-card" to={`${path}/auth`}>
          <span className="service-icon" aria-hidden="true">
            ◎
          </span>
          <h3>
            Authentication <span aria-hidden="true">↗</span>
          </h3>
          <p>Configure sign-up and sign-in for your application.</p>
          <span className="service-meta">
            {auth.loading
              ? 'Loading status…'
              : auth.error
                ? 'Status unavailable'
                : auth.data?.enabled
                  ? 'Enabled · email and password'
                  : 'Not enabled'}
          </span>
        </Link>
        <Link className="service-card" to={`${path}/domains`}>
          <span className="service-icon" aria-hidden="true">
            ◇
          </span>
          <h3>
            Domains <span aria-hidden="true">↗</span>
          </h3>
          <p>Review hostname availability, DNS and certificates.</p>
          <span className="service-meta">Check availability and setup</span>
        </Link>
      </div>
      <section className="card panel" aria-labelledby="backend-capabilities-heading">
        <h2 id="backend-capabilities-heading">Backend capabilities</h2>
        {capabilities.loading ? <p role="status">Loading capabilities…</p>
          : capabilities.error ? <ErrorBox retry={capabilities.reload}>{capabilities.error}</ErrorBox>
          : capabilities.data && <>
            <p className="sub">{capabilities.data.assessment?.note ?? 'Configuration only; run diagnostics to check your backend.'}</p>
            <dl>
              {capabilities.data.services.map((service) => <div key={service.id ?? service.name}>
                <dt><strong>{service.name}</strong> · {service.state?.replaceAll('_', ' ') ?? (service.available ? 'configured' : 'unavailable')}</dt>
                <dd>{service.note ?? service.reason} <span className="sub">{service.how}</span></dd>
              </div>)}
            </dl>
          </>}
      </section>
      <div className="overview-lower">
        <section className="card panel">
          <div className="section-head">
            <h2>Connect to your project</h2>
            <Link to={`${path}/keys`}>API keys ↗</Link>
          </div>
          <p className="sub">Use this endpoint in your application and tools.</p>
          {p.dataPlaneUrl ? (
            <div className="endpoint">
              <code>{p.dataPlaneUrl}</code>
              <CopyButton value={p.dataPlaneUrl} />
            </div>
          ) : (
            <p className="notice">No endpoint is configured yet.</p>
          )}
          <details className="guide">
            <summary>Quickstart & API example</summary>
            <p className="sub">
              Run SQL with a server-only service key. Service access bypasses row policies. Never embed this
              key in browser code.
            </p>
            <div className="code">
              <pre>
                <code>{snippet}</code>
              </pre>
              <CopyButton value={snippet} label="Copy example" />
            </div>
            <p>
              <Link to="/connect">Connect your coding agent →</Link>
            </p>
          </details>
        </section>
        <section className="card panel">
          <h2>Next steps</h2>
          <ul className="next-steps">
            <li>
              <Link to={`${path}/database`}>
                Explore your database <span>Tables, structure and policies →</span>
              </Link>
            </li>
            <li>
              <Link to={`${path}/keys`}>
                Connect your application <span>Create an appropriate API key →</span>
              </Link>
            </li>
            <li>
              <Link to={`${path}/diagnostics`}>
                Check project configuration <span>Find issues and suggested fixes →</span>
              </Link>
            </li>
          </ul>
        </section>
      </div>
      <details className="guide card panel">
        <summary>Available AI models</summary>
        {models.loading ? (
          <p role="status">Loading model catalogue…</p>
        ) : models.error ? (
          <ErrorBox retry={models.reload}>{models.error}</ErrorBox>
        ) : !models.data?.available ? (
          <p className="sub">The model catalogue is unavailable in this environment.</p>
        ) : (
          <>
            <p className="sub">
              Model availability does not verify a successful inference or configured billing. Calls require a
              service key.
            </p>
            <div className="table-scroll">
              <table className="grid">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Model</th>
                    <th>Context</th>
                    <th>Use</th>
                  </tr>
                </thead>
                <tbody>
                  {models.data.models.map((m) => (
                    <tr key={m.name}>
                      <td>{m.aliases?.[0] ?? m.name}</td>
                      <td>
                        <code>{m.name}</code>
                      </td>
                      <td>{m.contextTokens.toLocaleString()}</td>
                      <td className="wrap-cell">{m.describes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </details>
    </>
  )
}
